import {
  Box,
  Dialog,
  Button,
  Backdrop,
  useTheme,
  TextField,
  IconButton,
  DialogTitle,
  Autocomplete,
  DialogContent,
  svgIconClasses,
  CircularProgress,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { Version } from 'library/types';
import apiService from 'library/api-service';
import { FormattedMessage } from 'react-intl';
import CloseIcon from '@mui/icons-material/Close';
import { gray } from 'providers/theme/themePrimitives';
import ReactDiffViewer from 'react-diff-viewer-continued';
import loaderLogoSrc from 'assets/images/idecon-loader-logo.png';
import { renderAutoCompleteLocaleText, renderFormattedDate } from 'utils';
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';

type ShowVersionsProps = {
  open: boolean;
  selectedBookId: number | null;
  setSelectedBookId: Dispatch<SetStateAction<number | null>>;
  handleClose: () => void;
};

const ShowVersions = ({ open, selectedBookId, setSelectedBookId, handleClose: closeDialog }: ShowVersionsProps) => {
  // other hooks
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  // useState hooks
  const [oldCode, setOldCode] = useState('');
  const [newCode, setNewCode] = useState('');
  const [versions, setVersions] = useState<Version[]>([]);
  const [loadingCompare, setLoadingCompare] = useState(false);
  const [loadingRestore, setLoadingRestore] = useState(false);
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState<Version | null>(null);

  // async helper functions
  const getVersionsByBookId = useCallback(async () => {
    try {
      setLoadingVersions(true);

      const response = await apiService.getVersionsByBookId(selectedBookId as number);
      const duplicateArr = [...response.data];
      const sortedData = duplicateArr.sort(
        (a, b) => new Date(b.changedDate).getTime() - new Date(a.changedDate).getTime()
      );

      setVersions(sortedData);
    } catch {
    } finally {
      setLoadingVersions(false);
    }
  }, [selectedBookId]);

  const getComparasions = useCallback(async () => {
    try {
      setLoadingCompare(true);

      const response = await apiService.getComparasionsByVersionId(selectedVersion?.id as number);

      setOldCode(response.data.previous);
      setNewCode(response.data.current);
    } catch {
    } finally {
      setLoadingCompare(false);
    }
  }, [selectedVersion]);

  const restore = async () => {
    if (!selectedVersion)
      return enqueueSnackbar(
        <FormattedMessage id="dashboard.requiredSelectionOfVersion" defaultMessage="Versiyon seçilmelidir!" />,
        {
          variant: 'warning',
        }
      );

    try {
      setLoadingRestore(true);

      const response = await apiService.restoreVersion(selectedVersion.id);

      if (response.isSuccess) {
        enqueueSnackbar(
          <FormattedMessage
            id="dashboard.restoreVersionCompleted"
            defaultMessage="Versiyon Geçişi Başarıyla Gerçekleştirildi"
          />,
          {
            variant: 'success',
          }
        );

        setSelectedBookId(null);
        handleClose();
      }
    } catch {
    } finally {
      setLoadingRestore(false);
    }
  };

  // sync helper functions
  const handleClose = () => {
    closeDialog();
    setSelectedVersion(null);
    setOldCode('');
    setNewCode('');
  };

  // useEffect hooks
  useEffect(() => {
    if (selectedBookId && open) {
      getVersionsByBookId();
    }
  }, [selectedBookId, open, getVersionsByBookId]);

  useEffect(() => {
    if (selectedVersion && open) {
      getComparasions();
    }
  }, [selectedVersion, open, getComparasions]);

  return (
    <Dialog maxWidth="xl" disableRestoreFocus open={open} onClose={handleClose}>
      <DialogTitle>
        <FormattedMessage id="dashboard.editHistory" defaultMessage="Düzenleme Geçmişi" />
      </DialogTitle>
      <Box display="flex" justifyContent="center" alignItems="center" gap={10} marginInline={3} marginBottom={2}>
        <Autocomplete
          size="small"
          fullWidth
          options={versions}
          loading={loadingVersions}
          value={selectedVersion}
          {...renderAutoCompleteLocaleText()}
          onChange={(_, newValue: Version | null) => {
            setSelectedVersion(newValue);
          }}
          getOptionLabel={(option: Version) => `${renderFormattedDate(option.changedDate, true)} ( ${option.user} )`}
          renderInput={(params) => (
            <TextField
              {...params}
              sx={{
                minWidth: { md: 600 },
                '& .MuiButtonBase-root': {
                  border: 'none',
                  backgroundColor: 'inherit',
                  width: '2.25rem',
                  height: '2.25rem',
                  padding: '0.25rem',
                  [`& .${svgIconClasses.root}`]: { fontSize: '1rem' },
                  '&:hover': {
                    backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[800],
                  },
                },
              }}
              label={<FormattedMessage id="dashboard.edits" defaultMessage="Düzenlemeler" />}
              variant="outlined"
              fullWidth
              size="small"
              slotProps={{
                input: {
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingVersions ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                },
              }}
            />
          )}
        />
        <Button onClick={restore} variant="contained" sx={{ minWidth: { md: 200 }, minHeight: 40 }}>
          <FormattedMessage id="dashboard.restore" defaultMessage="Seçili Versiyon'a Geç" />
        </Button>
      </Box>
      <IconButton
        aria-label="close"
        onClick={handleClose}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}>
        <CloseIcon />
      </IconButton>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
        {selectedVersion && oldCode && newCode && (
          <ReactDiffViewer
            oldValue={oldCode}
            newValue={newCode}
            splitView={true}
            codeFoldMessageRenderer={() => (
              <FormattedMessage id="dashboard.expandLines" defaultMessage="Satırları Genişlet" />
            )}
            onLineNumberClick={() => {}}
          />
        )}
      </DialogContent>
      <Backdrop
        sx={{ display: 'flex', flexDirection: 'column', color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loadingCompare || loadingRestore}>
        <img src={loaderLogoSrc} alt="loader-logo" />
        <CircularProgress color="inherit" />
      </Backdrop>
    </Dialog>
  );
};

export default ShowVersions;
